/**
 * TrialBanner — top-of-AppShell countdown for the 14-day Pro trial.
 *
 * Mirrors VipTrialBanner (which is for VIP comp grants) but reads from
 * the new `user.trial` payload added by `_enrich_user()`:
 *   {
 *     on_trial:        bool — currently inside the trial window
 *     expired:         bool — trial window has elapsed
 *     days_remaining:  int  — full days left
 *     expires_at:      str  — ISO timestamp
 *     source:          str  — 'signup' | 'affiliate' | 'founder_access' | 'migration_from_free'
 *   }
 *
 * Color escalation:
 *   - >7 days  → silent (no banner — they have plenty of time)
 *   - 4-7 days → emerald (neutral, just FYI)
 *   - 2-3 days → amber (warn)
 *   - 0-1 day  → rose (urgent)
 *   - expired  → rose with "Reactivate to keep your data" CTA
 */
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, AlertTriangle, Clock, Rocket } from "lucide-react";

const TONE = {
  info: {
    bg: "bg-emerald-50 border-emerald-200 text-emerald-900",
    ctaBg: "bg-emerald-600 hover:bg-emerald-700",
    Icon: Sparkles,
  },
  warn: {
    bg: "bg-amber-50 border-amber-200 text-amber-900",
    ctaBg: "bg-amber-600 hover:bg-amber-700",
    Icon: Clock,
  },
  urgent: {
    bg: "bg-rose-50 border-rose-300 text-rose-900",
    ctaBg: "bg-rose-600 hover:bg-rose-700",
    Icon: AlertTriangle,
  },
  expired: {
    bg: "bg-rose-50 border-rose-300 text-rose-900",
    ctaBg: "bg-rose-600 hover:bg-rose-700",
    Icon: Rocket,
  },
};

// Always show the banner while on trial — visibility from day 1 builds the
// "upgrade or move on" pressure Tracy wants. Tone escalates as expiry nears.
function pickTone(days, expired) {
  if (expired) return "expired";
  if (days <= 1) return "urgent";
  if (days <= 3) return "warn";
  return "info";
}

export default function TrialBanner() {
  const { user } = useAuth();
  const trial = user?.trial;
  if (!trial) return null;

  // Paying users never see this — `get_trial_status()` returns on_trial=false
  // for active paid subs.
  if (!trial.on_trial && !trial.expired) return null;

  const days = Math.max(0, trial.days_remaining || 0);
  const toneKey = pickTone(days, trial.expired);
  if (!toneKey) return null;
  const tone = TONE[toneKey];

  let message;
  let ctaLabel = "Choose a plan →";
  if (trial.expired) {
    message = "Your Pro trial has ended. Your data is safe — pick a plan to reactivate AI features.";
    ctaLabel = "Reactivate Pro →";
  } else if (days <= 1) {
    message = "Last day of your Pro trial — pick a plan before midnight to keep AI features on.";
  } else if (days <= 3) {
    message = `Your Pro trial ends in ${days} days. Lock in your plan before the AI tools turn off.`;
  } else {
    message = `${days} days left in your Pro trial. Pick a plan anytime to lock in.`;
  }

  return (
    <div
      className={`border-b px-4 py-2.5 ${tone.bg}`}
      data-testid="trial-banner"
    >
      <div className="flex items-center gap-3 max-w-7xl mx-auto">
        <tone.Icon size={16} className="shrink-0" />
        <div className="text-sm font-medium flex-1 min-w-0">
          <span data-testid="trial-banner-message">{message}</span>{" "}
          <span className="opacity-80 hidden sm:inline">
            {trial.source === "affiliate" && "Thanks for joining via a referral — you got an extra week."}
            {trial.source === "founder_access" && "Founder Access bonus: 90 days of Pro, on the house."}
          </span>
        </div>
        <Link
          to="/v3/business/billing"
          className={`shrink-0 text-white font-bold text-xs px-3 py-1.5 rounded ${tone.ctaBg}`}
          data-testid="trial-banner-cta"
        >
          {ctaLabel}
        </Link>
      </div>
    </div>
  );
}
